/**
 * TanStack Query option factories for every admin endpoint. Follows the chat
 * feature's separated query/mutation-factory convention (conversations-query.ts):
 * keys + queryFn/mutationFn live here; the hooks in ./useAdmin*.ts wrap them.
 * EVERY call goes through the vendored `api` wrapper → the auth resolver seam.
 * No page builds its own fetch/MSAL (rebuild-lessons.md #2).
 */
import { api } from '@andavo/web-ui/api';
import type {
  Improvement,
  ImprovementActionRequest,
  ImprovementActionResponse,
  ImprovementStatus,
  AccessRequest,
  AccessRequestActionRequest,
  AccessRequestActionResponse,
  AccessRequestStatus,
  AdminUser,
  CronAuditResponse,
  FeatureFlag,
  FeatureFlagsResponse,
  GradeRequest,
  GradeResponse,
  KbStats,
  LlmBudget,
  OpsDashboard,
  SessionView,
  StatusPage,
  SystemInfo,
  UserRoleUpdateRequest,
  UserRoleUpdateResponse,
  UserRolesUpdateRequest,
  UserRolesUpdateResponse,
  InviteUserRequest,
  InviteUserResponse,
} from '../types';

export const adminQueries = {
  opsDashboard: () => ({
    queryKey: ['admin', 'ops-dashboard'] as const,
    queryFn: () => api.get<OpsDashboard>('api/admin/ops-dashboard'),
  }),
  systemInfo: () => ({
    queryKey: ['admin', 'system-info'] as const,
    queryFn: () => api.get<SystemInfo>('api/admin/system-info'),
  }),
  featureFlags: () => ({
    queryKey: ['admin', 'feature-flags'] as const,
    queryFn: () => api.get<FeatureFlagsResponse>('api/admin/feature-flags'),
  }),
  cronAudit: (job: string | null, limit: number) => ({
    queryKey: ['admin', 'cron-audit', job ?? '', limit] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      if (job) params.set('job', job);
      params.set('limit', String(limit));
      return api.get<CronAuditResponse>(
        `api/admin/cron-audit?${params.toString()}`,
      );
    },
  }),
  llmBudget: () => ({
    queryKey: ['admin', 'llm-budget'] as const,
    queryFn: () => api.get<LlmBudget>('api/admin/llm-budget'),
    // The page auto-refreshes; keep it fresh-ish.
    refetchInterval: 15_000,
  }),
  kbStats: () => ({
    queryKey: ['admin', 'kb-stats'] as const,
    queryFn: () => api.get<KbStats>('api/admin/kb-stats'),
  }),
  /**
   * session-view is parameterised and only runs once a user has been entered —
   * `enabled: Boolean(user)` keeps it from firing on an empty form.
   */
  sessionView: (user: string, windowMinutes: number) => ({
    queryKey: ['admin', 'session-view', user, windowMinutes] as const,
    queryFn: () => {
      const params = new URLSearchParams({
        user,
        windowMinutes: String(windowMinutes),
      });
      return api.get<SessionView>(
        `api/admin/session-view?${params.toString()}`,
      );
    },
    enabled: Boolean(user),
    retry: false,
  }),
  /** Public status endpoint (no admin gate on the API), shown inside the admin shell. */
  statusPage: () => ({
    queryKey: ['admin', 'status-page'] as const,
    queryFn: () => api.get<StatusPage>('api/status/page'),
    refetchInterval: 30_000,
  }),
  /**
   * Access requests (the legacy admin.js "requests" tab). GET against the
   * shared /api/chat/users handler with ?admin=true&requests=true. Optional
   * status filter ('' = all). `status` is folded into the query key so the
   * filtered + unfiltered lists cache independently.
   */
  accessRequests: (status: AccessRequestStatus | '') => ({
    queryKey: ['admin', 'access-requests', status] as const,
    queryFn: () => {
      const params = new URLSearchParams({ admin: 'true', requests: 'true' });
      if (status) params.set('status', status);
      return api.get<AccessRequest[]>(`api/chat/users?${params.toString()}`);
    },
  }),
  /**
   * Improvement suggestions (2026-06-06 de-GitHub): product data in PG,
   * triaged here instead of as GitHub issues nobody could file.
   */
  improvements: (status: ImprovementStatus | '') => ({
    queryKey: ['admin', 'improvements', status] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      const qs = params.toString();
      return api.get<Improvement[]>(
        `api/admin/improvements${qs ? `?${qs}` : ''}`,
      );
    },
  }),
  /**
   * User management list (the legacy admin.js "users" tab). GET against the
   * dedicated /api/admin/users endpoint (NOT the legacy chat handler). The
   * default roster HIDES system/service/test accounts (#1377) — the canonical
   * filter lives server-side in api/admin/users.js. `role` comes back derived
   * from the authoritative public.user_roles table, never client-mutable
   * metadata.
   */
  adminUsers: () => ({
    queryKey: ['admin', 'users'] as const,
    queryFn: () => api.get<AdminUser[]>('api/admin/users'),
  }),
  /**
   * Admin-only EXACT-email lookup. The default roster hides system/test
   * accounts; this is the narrow, default-off path back to a single hidden
   * account (e.g. the e2e test member the parity grant/revoke spec targets).
   * The server matches on the FULL email only (no prefix/substring), returns an
   * array of 0-or-1 users, and audits the access. `enabled: Boolean(email)`
   * keeps it from firing on an empty search box.
   */
  adminUserByEmail: (email: string) => ({
    queryKey: ['admin', 'users', 'lookup', email] as const,
    queryFn: () =>
      api.get<AdminUser[]>(
        `api/admin/users?email=${encodeURIComponent(email)}`,
      ),
    enabled: Boolean(email),
    retry: false,
  }),
};

/**
 * Boolean feature-flag toggle (PATCH). Scoped intentionally to the boolean
 * toggle the task calls for — the full create/edit/delete modal is out of scope
 * for this page.
 */
export const adminMutations = {
  toggleFlag: () => ({
    mutationFn: ({ name, value }: { name: string; value: boolean }) =>
      api.patch<{ flag: FeatureFlag }>('api/admin/feature-flags', {
        name,
        value,
      }),
  }),
  grade: () => ({
    mutationFn: (body: GradeRequest) =>
      api.post<GradeResponse>('api/admin/grade', body),
  }),
  /**
   * Open a GitHub PR from reviewed KB markdown. This step IS JSON, so it goes
   * through the vendored api client (unlike the multipart PDF upload, which
   * lives in ./kb-upload.ts). Mirrors POST /api/admin/kb/upload?action=pr.
   */
  kbPr: () => ({
    mutationFn: (body: { markdown: string; slug: string; branch?: string }) =>
      api.post<{ prUrl?: string; prNumber?: number; branch?: string }>(
        'api/admin/kb/upload?action=pr',
        body,
      ),
  }),
  /**
   * Approve (→ sends Entra invite) or deny a single access request. POSTs to the
   * shared handler's requests sub-route. The body carries request_id + action;
   * the server is authoritative on whether the request is still pending (returns
   * 400 "already <status>" on a stale double-click — surfaced as an ApiError).
   */
  accessRequestAction: () => ({
    mutationFn: (body: AccessRequestActionRequest) =>
      api.post<AccessRequestActionResponse>(
        'api/chat/users?admin=true&requests=true',
        body,
      ),
  }),
  /** Close (with optional comment) or reopen an improvement suggestion. */
  improvementAction: () => ({
    mutationFn: (body: ImprovementActionRequest) =>
      api.post<ImprovementActionResponse>('api/admin/improvements', body),
  }),
  /**
   * Grant ('admin') or revoke ('user') admin access for a user. PATCHes the
   * dedicated /api/admin/users endpoint (NOT the legacy chat handler); the
   * server persists the change to public.user_roles and busts its admin cache
   * so the grant/revoke takes effect immediately.
   */
  setUserRole: () => ({
    mutationFn: (body: UserRoleUpdateRequest) =>
      api.patch<UserRoleUpdateResponse>('api/admin/users', body),
  }),
  /**
   * Invite a person AND pre-set their access in ONE call.
   *   POST /api/admin/users { email, roles[] , name? }
   *
   * Inviting and granting used to be two separate places, and in the gap between
   * them the invited person could sign in to an app where every surface was closed
   * (default-deny) — which reads to them as a broken invite. The server sends the
   * Graph invitation first (it mints the identity whose oid the roles are keyed to),
   * then writes the roles.
   *
   * 201 = invited + access set. 207 = invited but the grant failed, with
   * `roles_error` explaining; the email HAS gone out either way, so the caller must
   * surface 207 as a partial success rather than a clean one.
   */
  inviteUser: () => ({
    mutationFn: (body: InviteUserRequest) =>
      api.post<InviteUserResponse>('api/admin/users', body),
  }),
  /**
   * Unified role-SET write (role-primary model, rbac-model.json). Replaces the
   * user's WHOLE set of stored role strings in one PATCH:
   *   PATCH /api/admin/users { user_id, roles: ['admin','crm:csm', …] }
   * `roles: []` removes all access (default-DENY). The server persists to
   * experts.user_roles and busts its admin cache, so the change takes effect
   * immediately. Authorities are DERIVED from roles — they are never sent.
   *
   * With `baseline_roles` (the set the editor loaded) the PATCH is a DIFF instead
   * of a replace: only roles added or removed relative to the baseline change,
   * so a stale editor cannot revert another admin's concurrent grant or revoke.
   * The page-access editor always sends it; the response `roles` is the
   * authoritative stored set either way.
   */
  setUserRoles: () => ({
    mutationFn: (body: UserRolesUpdateRequest) =>
      api.patch<UserRolesUpdateResponse>('api/admin/users', body),
  }),
};
